// src/hooks/useProductDetailsMaster.js
import { useEffect, useState, useCallback } from "react";
import CommonService from "../services/common.service";

function firstNonEmpty(...values) {
  for (const value of values) {
    if (value === undefined || value === null) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return "";
}

export function useProductDetailsMaster() {
  const [keywords, setKeywords] = useState([]);
  const [customizationTypes, setCustomizationTypes] = useState([]);
  const [dimensionUnit, setDimensionUnit] = useState([]);
  const [attributeUnit, setAttributeUnit] = useState([]);
  const [packagingMaterials, setPackagingMaterials] = useState([]);
  const [labelling, setLabelling] = useState([]);
  const [inventoryStatus, setInventoryStatus] = useState([]);
  const [packingTypes, setPackingTypes] = useState([]);
  const [loadingPackingTypes, setLoadingPackingTypes] = useState(false);
  const [productCategories, setProductCategories] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [subCategories, setSubCategories] = useState([]);
  const [loadingSubCategories, setLoadingSubCategories] = useState(false);
  const [subSubCategories, setSubSubCategories] = useState([]);
  const [loadingSubSubCategories, setLoadingSubSubCategories] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fetch product categories with hierarchical structure from separate tables
  useEffect(() => {
    async function fetchCategories() {
      try {
        setLoadingCategories(true);
        // Fetch top-level categories from master_category table
        const categoriesResponse = await CommonService.get("/master/master-categories");
        
        if (categoriesResponse && categoriesResponse.success && categoriesResponse.data) {
          // Handle both array and object response formats
          let topLevelCategories = [];
          if (Array.isArray(categoriesResponse.data)) {
            topLevelCategories = categoriesResponse.data;
          } else if (categoriesResponse.data.categories || categoriesResponse.data.master_categories) {
            topLevelCategories = categoriesResponse.data.categories || categoriesResponse.data.master_categories || [];
          } else if (categoriesResponse.data) {
            topLevelCategories = [categoriesResponse.data];
          }
          
          console.log("Top level categories fetched:", topLevelCategories.length);
          
          // If no categories found, set empty array and return early
          if (topLevelCategories.length === 0) {
            console.warn("No categories found in response");
            setCategoryData([]);
            setLoadingCategories(false);
            return;
          }
          
          // Build hierarchical structure: fetch subcategories for each category from master_sub_category table
          const categoriesWithSubs = await Promise.all(
            topLevelCategories.map(async (category) => {
              const categoryId = category.category_id || category.id;
              const categoryName = category.category || category.category_name || category.name;
              
              // Fetch subcategories for this category from master_sub_category table
              let subcategories = [];
              try {
                // Validate categoryId is a valid number
                const numericCategoryId = parseInt(categoryId);
                if (isNaN(numericCategoryId) || numericCategoryId <= 0) {
                  console.warn(`Invalid category ID: ${categoryId} for category: ${categoryName}`);
                  return {
                    id: String(categoryId),
                    name: categoryName,
                    subs: []
                  };
                }
                
                const subsResponse = await CommonService.get(`/master/master-sub-categories/${numericCategoryId}`);
                if (subsResponse && subsResponse.success && subsResponse.data) {
                  const subs = Array.isArray(subsResponse.data) 
                    ? subsResponse.data 
                    : [subsResponse.data];
                  
                  // Fetch sub-subcategories for each subcategory from master_sub_sub_category table
                  // Use Promise.allSettled to continue even if some requests fail
                  const subcategoryPromises = subs.map(async (sub) => {
                    const subId = sub.sub_category_id || sub.id;
                    const subName = sub.sub_category || sub.sub_category_name || sub.name;
                    
                    // Validate subId is a valid number
                    const numericSubId = parseInt(subId);
                    if (isNaN(numericSubId) || numericSubId <= 0) {
                      console.warn(`Invalid subcategory ID: ${subId} for subcategory: ${subName}`);
                      return {
                        id: String(subId),
                        name: subName,
                        subs: []
                      };
                    }
                    
                    // Fetch sub-subcategories from master_sub_sub_category table
                    // Note: We skip loading sub-subcategories during initial load to avoid network errors
                    // They will be loaded on-demand when a subcategory is selected
                    let subSubcategories = [];
                    
                    // Skip loading sub-subcategories during initial category load
                    // They will be loaded dynamically when needed via fetchSubSubCategories
                    // This prevents network errors from blocking the entire category loading process
                    
                    return {
                      id: String(subId),
                      name: subName,
                      subs: subSubcategories
                    };
                  });
                  
                  // Use allSettled to continue even if some requests fail
                  const subcategoryResults = await Promise.allSettled(subcategoryPromises);
                  subcategories = subcategoryResults
                    .map(result => result.status === 'fulfilled' ? result.value : null)
                    .filter(sub => sub && sub.id); // Filter out null/error entries
                }
              } catch (subsErr) {
                console.error(`Error fetching subcategories for category ${categoryId}:`, subsErr);
              }
              
              return {
                id: String(categoryId),
                name: categoryName,
                subs: subcategories
              };
            })
          );
          
          setProductCategories(topLevelCategories);
          setCategoryData(categoriesWithSubs);
          console.log("Categories loaded:", categoriesWithSubs.length, categoriesWithSubs);
        } else {
          console.warn("No categories data in response:", categoriesResponse);
        }
      } catch (error) {
        console.error("Error fetching product categories from API:", error);
        setCategoryData([]);
      } finally {
        setLoadingCategories(false);
      }
    }
    
    fetchCategories();
  }, []);

  useEffect(() => {
    async function fetchData() {
      try {
        const response = await CommonService.get("/master/product-catalogue-details");
        
        // Extract data from response
        if (response && response.success && response.data) {
          const apiKeywords = response.data.master_keywords || [];
          const apiCustomizationTypes = response.data.master_customization_types || [];
          const apiDimensionUnit = response.data.master_diamension_unit || [];
          const apiAttributeUnit = response.data.master_attribute_unit || [];
          const apiPackagingMaterials = response.data.master_packaging_materials || [];
          const apiLabelling = response.data.master_labelling || [];
          const apiInventoryStatus = response.data.master_inventory_status || [];
          
          setKeywords(apiKeywords);
          setCustomizationTypes(apiCustomizationTypes);
          setDimensionUnit(apiDimensionUnit);
          setAttributeUnit(apiAttributeUnit);
          setPackagingMaterials(apiPackagingMaterials);
          setLabelling(apiLabelling);
          setInventoryStatus(apiInventoryStatus);
        }
      } catch (error) {
        // Handle network errors gracefully
        if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
          console.warn("Backend server is not available. Product catalogue details will not be loaded.", {
            message: "Please ensure the backend server is running on http://localhost:3000",
            error: error.message
          });
          // Set empty arrays as fallback so the app continues to work
          setKeywords([]);
          setCustomizationTypes([]);
          setDimensionUnit([]);
          setAttributeUnit([]);
          setPackagingMaterials([]);
          setLabelling([]);
          setInventoryStatus([]);
        } else {
          console.error("Error fetching product details from API:", error);
          // Set empty arrays as fallback
          setKeywords([]);
          setCustomizationTypes([]);
          setDimensionUnit([]);
          setAttributeUnit([]);
          setPackagingMaterials([]);
          setLabelling([]);
          setInventoryStatus([]);
        }
      } finally {
        setLoading(false);
      }
    }
    
    fetchData();
  }, []);

  // Function to fetch packing types based on packaging materials ID
  const fetchPackingTypes = async (packagingMaterialsId) => {
    if (!packagingMaterialsId) {
      setPackingTypes([]);
      return;
    }

    try {
      setLoadingPackingTypes(true);
      const response = await CommonService.get(`/master/product-packing-type/${packagingMaterialsId}`);
      
      if (response && response.success && response.data) {
        const apiPackingTypes = response.data.master_packing_type || response.data.packing_types || response.data || [];
        
        setPackingTypes(apiPackingTypes);
      } else {
        setPackingTypes([]);
      }
    } catch (error) {
      console.error("Error fetching packing types from API:", error);
      setPackingTypes([]);
    } finally {
      setLoadingPackingTypes(false);
    }
  };

  // Function to fetch subcategories based on category ID (memoized to prevent infinite loops)
  const fetchSubCategories = useCallback(async (categoryId) => {
    if (!categoryId) {
      setSubCategories([]);
      return;
    }

    try {
      setLoadingSubCategories(true);
      const response = await CommonService.get(`/master/master-sub-categories/${categoryId}`);
      
      if (response && response.success && response.data) {
        const subs = Array.isArray(response.data) ? response.data : [response.data];
        const mappedSubs = subs.map(sub => ({
          id: String(sub.sub_category_id || sub.id),
          name: sub.sub_category || sub.sub_category_name || sub.name,
          value: String(sub.sub_category_id || sub.id),
          label: sub.sub_category || sub.sub_category_name || sub.name
        }));
        setSubCategories(mappedSubs);
      } else {
        setSubCategories([]);
      }
    } catch (error) {
      console.error("Error fetching subcategories from API:", error);
      setSubCategories([]);
    } finally {
      setLoadingSubCategories(false);
    }
  }, []);

  // Function to fetch sub-subcategories based on subcategory ID (memoized to prevent infinite loops)
  const fetchSubSubCategories = useCallback(async (subCategoryId) => {
    if (!subCategoryId) {
      setSubSubCategories([]);
      return;
    }

    try {
      setLoadingSubSubCategories(true);
      const response = await CommonService.get(`/master/master-sub-sub-categories/${subCategoryId}`);
      
      if (response && response.success && response.data) {
        const subSubs = Array.isArray(response.data) ? response.data : [response.data];
        const mappedSubSubs = subSubs.map(subSub => ({
          id: String(subSub.sub_sub_category_id || subSub.id),
          name: subSub.sub_sub_category || subSub.name || subSub.label,
          value: String(subSub.sub_sub_category_id || subSub.id),
          label: subSub.sub_sub_category || subSub.name || subSub.label
        }));
        setSubSubCategories(mappedSubSubs);
      } else {
        setSubSubCategories([]);
      }
    } catch (error) {
      console.error("Error fetching sub-subcategories from API:", error);
      setSubSubCategories([]);
    } finally {
      setLoadingSubSubCategories(false);
    }
  }, []);

  // Map keywords to react-select format { value, label }
  const keywordsOptions = keywords.map((kw, index) => {
    const value = kw.keyword_id || kw.id || kw.value;
    const label = kw.keyword || kw.keyword_name || kw.label || kw.name;
    return { value, label };
  }).filter(opt => {
    const hasValue = opt.value !== undefined && opt.value !== null && String(opt.value).trim() !== '';
    const hasLabel = opt.label !== undefined && opt.label !== null && String(opt.label).trim() !== '';
    return hasValue && hasLabel;
  });

  // Map customization types to react-select format { value, label }
  // API format: {customization_id: 1, customization: 'Minor customization', flag: 1}
  const customizationTypesOptions = customizationTypes.map((ct, index) => {
    const value = ct.customization_id || ct.customization_type_id || ct.id || ct.value;
    const label = ct.customization || ct.customization_type || ct.type_name || ct.label || ct.name;
    return { value, label };
  }).filter(opt => {
    const hasValue = opt.value !== undefined && opt.value !== null && String(opt.value).trim() !== '';
    const hasLabel = opt.label !== undefined && opt.label !== null && String(opt.label).trim() !== '';
    return hasValue && hasLabel;
  });

  // Map dimension unit to react-select format { value, label }
  // API format: {dimention_unit_id: 1, dimention_unit: 'mm', flag: 1}
  // Note: API uses "dimention" (with 'e') not "dimension" (with 'a')
  const dimensionUnitOptions = dimensionUnit.map((du, index) => {
    const value = du.dimention_unit_id || du.dimension_unit_id || du.unit_id || du.id || du.value;
    const label = du.dimention_unit || du.dimension_unit || du.unit_name || du.unit || du.label || du.name;
    return { value, label };
  }).filter(opt => {
    const hasValue = opt.value !== undefined && opt.value !== null && String(opt.value).trim() !== '';
    const hasLabel = opt.label !== undefined && opt.label !== null && String(opt.label).trim() !== '';
    return hasValue && hasLabel;
  });

  // Map attribute unit to react-select format { value, label }
  const attributeUnitOptions = attributeUnit.map((au, index) => {
    const value = au.attribute_unit_id || au.unit_id || au.id || au.value;
    const label = firstNonEmpty(
      au.attribute_unit,
      au.unit_name,
      au.unit,
      au.uom,
      au.uom_name,
      au.attribute_uom,
      au.attribute_value,
      au.label,
      au.name
    );
    return { value, label, originalIndex: index, originalData: au };
  }).filter((opt, filterIndex) => {
    const hasValue = opt.value !== undefined && opt.value !== null && String(opt.value).trim() !== '';
    const hasLabel = opt.label !== undefined && opt.label !== null && String(opt.label).trim() !== '';
    const isValid = hasValue && hasLabel;
    
    // Log filtered out items for debugging
    if (!isValid && process.env.NODE_ENV !== "production") {
      console.warn(`UOM item filtered out at index ${opt.originalIndex}:`, {
        value: opt.value,
        label: opt.label,
        originalData: opt.originalData,
        reason: !hasValue ? 'Missing or invalid value' : 'Missing or invalid label'
      });
    }
    
    return isValid;
  }).map(opt => {
    // Remove debug fields before returning
    const { originalIndex, originalData, ...cleanOpt } = opt;
    return cleanOpt;
  });

  // Map packaging materials to react-select format { value, label }
  // API format: {packing_materials_id: 1, packing_materials: 'Primary Packing Material', flag: 1}
  const packagingMaterialsOptions = packagingMaterials.map((pm, index) => {
    const value = pm.packing_materials_id || pm.packaging_material_id || pm.material_id || pm.id || pm.value;
    const label = pm.packing_materials || pm.packaging_material || pm.material_name || pm.material || pm.label || pm.name;
    return { value, label };
  }).filter(opt => {
    const hasValue = opt.value !== undefined && opt.value !== null && String(opt.value).trim() !== '';
    const hasLabel = opt.label !== undefined && opt.label !== null && String(opt.label).trim() !== '';
    return hasValue && hasLabel;
  });

  // Map labelling to react-select format { value, label }
  const labellingOptions = labelling.map((lbl, index) => {
    const value = lbl.labelling_id || lbl.label_id || lbl.id || lbl.value;
    const label = lbl.labelling || lbl.label_name || lbl.label || lbl.name;
    return { value, label };
  }).filter(opt => {
    const hasValue = opt.value !== undefined && opt.value !== null && String(opt.value).trim() !== '';
    const hasLabel = opt.label !== undefined && opt.label !== null && String(opt.label).trim() !== '';
    return hasValue && hasLabel;
  });

  // Map inventory status to react-select format { value, label }
  const inventoryStatusOptions = inventoryStatus.map((is, index) => {
    const value = is.inventory_status_id || is.status_id || is.id || is.value;
    const label = is.inventory_status || is.status_name || is.status || is.label || is.name;
    return { value, label };
  }).filter(opt => {
    const hasValue = opt.value !== undefined && opt.value !== null && String(opt.value).trim() !== '';
    const hasLabel = opt.label !== undefined && opt.label !== null && String(opt.label).trim() !== '';
    return hasValue && hasLabel;
  });

  // Map packing types to react-select format { value, label }
  const packingTypesOptions = packingTypes.map((pt, index) => {
    const value = pt.packing_type_id || pt.packing_typeId || pt.id || pt.value;
    const label = pt.packing_type || pt.packingType || pt.type_name || pt.type || pt.label || pt.name;
    return { value, label };
  }).filter(opt => {
    const hasValue = opt.value !== undefined && opt.value !== null && String(opt.value).trim() !== '';
    const hasLabel = opt.label !== undefined && opt.label !== null && String(opt.label).trim() !== '';
    return hasValue && hasLabel;
  });

  // Map subcategories to react-select format { value, label }
  const subCategoriesOptions = subCategories.map((sub) => ({
    value: sub.value || sub.id,
    label: sub.label || sub.name
  }));

  // Map sub-subcategories to react-select format { value, label }
  const subSubCategoriesOptions = subSubCategories.map((subSub) => ({
    value: subSub.value || subSub.id,
    label: subSub.label || subSub.name
  }));

  return {
    keywords,
    keywordsOptions,
    customizationTypes,
    customizationTypesOptions,
    dimensionUnit,
    dimensionUnitOptions,
    attributeUnit,
    attributeUnitOptions,
    packagingMaterials,
    packagingMaterialsOptions,
    labelling,
    labellingOptions,
    inventoryStatus,
    inventoryStatusOptions,
    packingTypes,
    packingTypesOptions,
    fetchPackingTypes,
    loadingPackingTypes,
    productCategories,
    categoryData,
    loadingCategories,
    subCategories,
    subCategoriesOptions,
    loadingSubCategories,
    fetchSubCategories,
    subSubCategories,
    subSubCategoriesOptions,
    loadingSubSubCategories,
    fetchSubSubCategories,
    loading
  };
}

